"use client"

import { Box } from "@chakra-ui/react"
import { PreviewAlertBox } from "components/Alert/PreviewAlert"
import { Homepage } from "packages/acquisition/components/chakra-components/Homepage"
import type { NextPage } from "next"
import React from "react"
import type { VariantType } from "types"
import type { ContentBlock } from "types/cms"
import { LandingPageProvider } from "packages/acquisition/components/chakra-components/LandingPage/LandingPageProvider"
import { useCMSPageContentTracking } from "lib/cms/hooks/useCMSPageContentTracking"
import { DefaultSchema } from "components/Seo/DefaultSchema"

interface HomePageProps {
  pageId: string
  blocks: ContentBlock[]
  preview: boolean
  variants: VariantType[]
  featuredProductSku?: string
  locale: "en-GB" | "en-US"
}

const NonMembersLandingPage: React.FC<HomePageProps> = ({
  pageId,
  blocks,
  variants,
  preview,
  featuredProductSku,
  locale,
}) => {
  return (
    <>
      <DefaultSchema locale={locale} />
      {preview && <PreviewAlertBox />}
      <LandingPageProvider
        pageId={pageId}
        featuredProductSku={featuredProductSku}
        variants={variants}
      >
        <Box minHeight="100vh">
          <Homepage blocks={blocks} isMember={false} />
        </Box>
      </LandingPageProvider>
    </>
  )
}

export const MembersLandingPage: React.FC<HomePageProps> = ({
  pageId,
  blocks,
  preview,
  variants,
  featuredProductSku,
}) => {
  return (
    <>
      {preview && <PreviewAlertBox />}
      <LandingPageProvider
        pageId={pageId}
        featuredProductSku={featuredProductSku}
        variants={variants}
      >
        <Box minHeight="100vh">
          <Homepage blocks={blocks} isMember />
        </Box>
      </LandingPageProvider>
    </>
  )
}

export const LandingPage: NextPage<
  HomePageProps & {
    isMember?: boolean
    selectedVariantIndex?: number
    experimentId?: string
  }
> = ({
  pageId,
  blocks,
  preview,
  variants,
  isMember,
  selectedVariantIndex,
  featuredProductSku,
  experimentId,
  locale,
}) => {
  useCMSPageContentTracking({
    isPreview: preview,
    experimentId,
    variation: selectedVariantIndex,
  })

  return isMember ? (
    <MembersLandingPage
      featuredProductSku={featuredProductSku}
      pageId={pageId}
      preview={preview}
      blocks={blocks}
      variants={variants}
      locale={locale}
    />
  ) : (
    <NonMembersLandingPage
      featuredProductSku={featuredProductSku}
      pageId={pageId}
      blocks={blocks}
      preview={preview}
      variants={variants}
      locale={locale}
    />
  )
}
